import { yupResolver } from '@hookform/resolvers/yup'
import { LineInfoCircle } from '@qr-front/icons/line-info-circle'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, useImperativeHandle } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { InferType } from 'yup'

import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'
import { maxLength, validateContentForm } from '@/utils'

import { GENERATOR_FORM_ANIMATION_DURATION } from '../components/GeneratorFormSection'
import { RhcField } from '../components/RhcField'
import { TContentFormHandle, TQrContentFormSection } from './qrContentFormTypes'
import { useSyncFormSectionValidity } from './useSyncFormSectionValidity'

export const SOCIAL_INFORMATION_FORM_ID = 'social-information'

export const socialInfoHeadlineAtom = atomWithReset('')
export const socialInfoDescriptionAtom = atomWithReset<string | undefined>('')

const SOCIAL_INFO_HEADLINE_MAX_LENGTH = 50
const SOCIAL_INFO_DESCRIPTION_MAX_LENGTH = 250

export const resetSocialInformationForm = () => {
	getStore().set(socialInfoHeadlineAtom, RESET)
	getStore().set(socialInfoDescriptionAtom, RESET)
}

export const getSocialInformationFormSchema = (t: TTranslationFunction) =>
	yup
		.object({
			headline: yup
				.string()
				.required(t('ui__field_validation_errors__generic__required')),

			description: yup.string().notRequired(),
		})
		.required()

export type TSocialInformationFields = InferType<
	ReturnType<typeof getSocialInformationFormSchema>
>

export const SocialInformationForm = forwardRef<TContentFormHandle>(
	(_, ref) => {
		const t = useTranslations()

		const form = useForm<TSocialInformationFields>({
			defaultValues: {
				headline: getStore().get(socialInfoHeadlineAtom),
				description: getStore().get(socialInfoDescriptionAtom),
			},
			mode: 'onTouched',
			resolver: yupResolver(getSocialInformationFormSchema(t)),
			reValidateMode: 'onChange',
			shouldUnregister: false,
		})

		const {
			formState: { isValid },
			handleSubmit,
		} = form

		useSyncFormSectionValidity({
			formId: SOCIAL_INFORMATION_FORM_ID,
			isValid,
		})

		useImperativeHandle(
			ref,
			() => ({
				validate: () =>
					validateContentForm(
						(onValid, onInvalid) =>
							handleSubmit(onValid, async (errors) => {
								onInvalid(errors)
							}),
						GENERATOR_FORM_ANIMATION_DURATION * 1.25,
					),
			}),
			[handleSubmit],
		)

		const headlineField = (
			<RhcField
				type="text"
				required
				form={form}
				path="headline"
				fieldName="headline"
				atom={socialInfoHeadlineAtom}
				label={t(
					'generator__content_form_section__social_information__headline__label',
				)}
				placeholder={t(
					'generator__content_form_section__social_information__headline__placeholder',
				)}
				className="w-full desktop:w-1/2"
				onChangeTransform={(event) =>
					maxLength(SOCIAL_INFO_HEADLINE_MAX_LENGTH)(event)
				}
			/>
		)

		const descriptionField = (
			<RhcField
				type="text"
				form={form}
				path="description"
				fieldName="description"
				atom={socialInfoDescriptionAtom}
				label={t(
					'generator__content_form_section__social_information__description__label',
				)}
				placeholder={t(
					'generator__content_form_section__social_information__description__placeholder',
				)}
				className="w-full desktop:w-1/2"
				onChangeTransform={(event) =>
					maxLength(SOCIAL_INFO_DESCRIPTION_MAX_LENGTH)(event)
				}
			/>
		)

		return (
			<fieldset className="flex flex-col gap-4 desktop:flex-row desktop:gap-12">
				{headlineField}
				{descriptionField}
			</fieldset>
		)
	},
)

SocialInformationForm.displayName = 'SocialInformationForm'

export const socialInformationFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: SOCIAL_INFORMATION_FORM_ID,
	title: t('generator__content_form_section__social_information__title'),
	description: t(
		'generator__content_form_section__social_information__description',
	),
	icon: <LineInfoCircle />,
	Content: SocialInformationForm,
})
